import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentStart } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingStartPageValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingStartPageContentSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingStartPageValidationErrors;
    updateDCUIContentStartPageFieldValue: (key: keyof DCUIContentStart, value: string) => void;
};

export const DCUIThemingStartPageContentSection = (props: DCUIThemingStartPageContentSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        validationErrors,
        updateDCUIContentStartPageFieldValue
    } = props;

    const handleHeadlineTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStartPageFieldValue("headline", event.target.value);
    };

    const handleResetHeadlineText = () => {
        updateDCUIContentStartPageFieldValue("headline", inheritedDCUIContent.start.headline);
    };

    const handleTitleTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStartPageFieldValue("title", event.target.value);
    };

    const handleResetTitleText = () => {
        updateDCUIContentStartPageFieldValue("title", inheritedDCUIContent.start.title);
    };

    const handleSubheadingTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStartPageFieldValue("subHeading", event.target.value);
    };

    const handleResetSubheadingText = () => {
        updateDCUIContentStartPageFieldValue("subHeading", inheritedDCUIContent.start.subHeading);
    };

    const handleExplanationTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStartPageFieldValue("explanationHtml", event.target.value);
    };

    const handleResetExplanationText = () => {
        updateDCUIContentStartPageFieldValue("explanationHtml", inheritedDCUIContent.start.explanationHtml);
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.start.headline}
                currentThemingPropertyValue={dcuiContent.start.headline}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"dcui-theming-start-page-headline-text-input"}
                    automationId={"dcui-theming-start-page-headline-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.headline.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.headline.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleHeadlineTextChange}
                    value={dcuiContent.start.headline || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.headline.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetHeadlineText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.headlineError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.start.title}
                currentThemingPropertyValue={dcuiContent.start.title}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"dcui-theming-start-page-title-text-input"}
                    automationId={"dcui-theming-start-page-title-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.title.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.title.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleTitleTextChange}
                    value={dcuiContent.start.title || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.title.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetTitleText}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.start.subHeading}
                currentThemingPropertyValue={dcuiContent.start.subHeading}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"dcui-theming-start-page-subheading-text-input"}
                    automationId={"dcui-theming-start-page-subheading-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.subheading.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.subheading.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleSubheadingTextChange}
                    value={dcuiContent.start.subHeading || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.subheading.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetSubheadingText}
                />
            </ThemingFormGroupWrapper>


            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.start.explanationHtml}
                currentThemingPropertyValue={dcuiContent.start.explanationHtml}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"dcui-theming-start-page-explanation-text-input"}
                    automationId={"dcui-theming-start-page-explanation-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.explanation.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.explanation.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleExplanationTextChange}
                    value={dcuiContent.start.explanationHtml || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.inputs.explanation.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetExplanationText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.explanationError}
        </div>
    );
};